import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { CheckCheck } from 'lucide-react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import {
  useMarkAllNotificationsRead,
  useMarkNotificationsRead,
  useNotificationList,
} from '@/hooks/useAdminNotifications'
import {
  NOTIFICATION_CATEGORIES,
  type AdminNotification,
  type NotificationCategory,
} from '@/lib/api/admin-notifications'
import { CATEGORY_LABELS, notificationHref } from '@/lib/notifications'
import { NotificationItem } from './NotificationItem'

const PANEL_PAGE_SIZE = 15

type Tab = 'all' | NotificationCategory

interface NotificationPanelProps {
  unreadByCategory?: Record<NotificationCategory, number>
  onNavigate: () => void
}

export function NotificationPanel({ unreadByCategory, onNavigate }: NotificationPanelProps) {
  const [tab, setTab] = useState<Tab>('all')
  const navigate = useNavigate()
  const markRead = useMarkNotificationsRead()
  const markAllRead = useMarkAllNotificationsRead()

  const category = tab === 'all' ? null : tab
  const { data, isLoading } = useNotificationList({ pageSize: PANEL_PAGE_SIZE, category })
  const notifications = data?.notifications ?? []

  function handleSelect(n: AdminNotification) {
    if (!n.is_read) markRead.mutate([n.id])
    const href = notificationHref(n)
    if (href) {
      onNavigate()
      navigate(href)
    }
  }

  return (
    <div className="flex flex-col overflow-hidden">
      <div className="flex items-center justify-between border-b px-3 py-2">
        <span className="text-sm font-semibold">Notifications</span>
        <button
          type="button"
          onClick={() => markAllRead.mutate(category)}
          disabled={markAllRead.isPending}
          className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
        >
          <CheckCheck className="h-3.5 w-3.5" />
          Mark all read
        </button>
      </div>

      {/* Plain buttons rather than <Tabs>: the list is a single query driven by
          `tab`, so Radix's per-tab content panels would just duplicate it. */}
      <div className="flex gap-1 border-b px-2 py-1.5">
        {(['all', ...NOTIFICATION_CATEGORIES] as Tab[]).map((t) => {
          const count = t === 'all'
            ? Object.values(unreadByCategory ?? {}).reduce((a, b) => a + b, 0)
            : (unreadByCategory?.[t] ?? 0)
          return (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={cn(
                'rounded-md px-2 py-1 text-xs font-medium transition-colors',
                tab === t
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {t === 'all' ? 'All' : CATEGORY_LABELS[t]}
              {count > 0 && <span className="ml-1 text-[10px] opacity-70">{count}</span>}
            </button>
          )
        })}
      </div>

      {/* Two Radix ScrollArea workarounds, both load-bearing:
          - the viewport's own `height: 100%` does not resolve here, so it grows
            to content height and paints over the footer; cap it directly.
          - the viewport wraps children in a `display: table` div that sizes to
            content, which breaks `truncate` on long subjects. Force it to block. */}
      <ScrollArea
        className="[&>[data-slot=scroll-area-viewport]]:max-h-[22rem] [&>[data-slot=scroll-area-viewport]>div]:!block"
      >
        {isLoading ? (
          <div className="space-y-2 p-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <p className="px-3 py-10 text-center text-sm text-muted-foreground">
            Nothing here yet.
          </p>
        ) : (
          <div className="divide-y">
            {notifications.map((n) => (
              <NotificationItem key={n.id} notification={n} onSelect={handleSelect} />
            ))}
          </div>
        )}
      </ScrollArea>

      <div className="border-t px-3 py-2">
        <Link
          to="/notifications"
          onClick={onNavigate}
          className="text-xs font-medium text-primary hover:underline"
        >
          View all notifications →
        </Link>
      </div>
    </div>
  )
}
